import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Text, 
  Flex, 
  Badge, 
  useColorModeValue, 
  Tooltip,
  IconButton,
  HStack,
  Spacer,
  Divider,
  List,
  ListItem,
  ListIcon
} from '@chakra-ui/react';
import { PhoneIcon, EditIcon, ChatIcon, CheckIcon, CalendarIcon, InfoIcon, WarningIcon, CloseIcon } from '@chakra-ui/icons';
import moment from 'moment';
import { useSocket } from '../../../../contexts/SocketContext';
import { formatIndianCurrency } from '../../../../utils/formatters';
import { useUsers } from '../context/UserContext';
import NewKBDragWrapper from './NewKBDragWrapper';
import NewKBRecordCallModal from './NewKBRecordCallModal';
import NewKBLeadModal from './NewKBLeadModal';
import api from '../../../../services/api';

const NewKBLeadCard = ({ lead, index, onLeadUpdate, isDragDisabled = false }) => {
  const { socket } = useSocket();
  const { getUserDisplayName } = useUsers();
  
  // Local state for real-time updates
  const [localCallCount, setLocalCallCount] = useState(lead.callCount || 0);
  const [localCommentCount, setLocalCommentCount] = useState(lead.commentCount || 0);
  const [localTaskCount, setLocalTaskCount] = useState(lead.taskCount || 0);
  const [localLastCallTime, setLocalLastCallTime] = useState(lead.lastCallTime || null);
  const [localNextFollowUpTime, setLocalNextFollowUpTime] = useState(
    lead.nextFollowUpTime || null
  );
  
  // Modal state
  const [isCallModalOpen, setIsCallModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  
  // Track last event timestamp for each lead to prevent race conditions
  const lastEventTimestampRef = useRef({});

  // Enhanced component rendering tracker
  const renderCount = React.useRef(0);
  useEffect(() => {
    renderCount.current += 1;
  });

  // Create a key that represents all lead data to detect any changes
  // This is critical for detecting prop changes
  const leadDataKey = JSON.stringify({
    id: lead.leadId || lead._id,
    name: lead.brideAndGroomName,
    callCount: lead.callCount,
    commentCount: lead.commentCount,
    taskCount: lead.taskCount,
    followUpTime: lead.nextFollowUpTime,
    lastUpdated: lead._lastUpdated, // Added by context to force updates
    updateKey: lead._updateKey, // Random key added by context
    budget: lead.budget?.amount,
    phone: lead.phone,
    email: lead.email,
    stage: lead.stage?._id
  });

  // Initialize props when card mounts
  useEffect(() => {
    // No need to log props on initial mount
  }, [lead.leadId]); // Only run when the card first mounts

  // Update local state whenever ANY lead property changes
  useEffect(() => {
    // Update local state when lead data changes
    // No need for verbose logging on every data change
    
    // CRITICAL: Always preserve existing counts if incoming data doesn't have them
    // or if our local value is higher (socket events may have updated it more recently)
    
    // For call count, check if the incoming value is valid and higher than current
    if (lead.callCount !== undefined && lead.callCount !== null) {
      const incomingCount = Number(lead.callCount);
      // Only update if incoming count is higher than what we have locally
      // This prevents props updates from overriding more recent socket updates
      if (incomingCount >= localCallCount) {
        setLocalCallCount(incomingCount);

      } else {

      }
    } else {
      // Preserve existing value if incoming is undefined/null
    }
    
    // For comment count, apply the same logic
    if (lead.commentCount !== undefined && lead.commentCount !== null) {
      setLocalCommentCount(Number(lead.commentCount));
    }
    
    // For task count, apply the same logic
    if (lead.taskCount !== undefined && lead.taskCount !== null) {
      setLocalTaskCount(Number(lead.taskCount));
    }
    
    // Always update follow-up time and last call time
    setLocalNextFollowUpTime(lead.nextFollowUpTime || null);
    setLocalLastCallTime(lead.lastCallTime || null);
  }, [leadDataKey, localCallCount, localCommentCount, localTaskCount]); // Use the stringified key to detect ANY changes
  
  // Handle successful call recording without reloading the page
  const handleCallSuccess = (updatedLead, callData) => {
    // DON'T update the call count locally - we'll rely on the server event
    // to provide the correct count to avoid double-counting
    
    // Update follow-up time if available
    if (updatedLead.nextFollowUpTime) {
      setLocalNextFollowUpTime(updatedLead.nextFollowUpTime);
    }
    
    // We don't need to emit socket events from the frontend anymore
    // The backend will emit the proper events with the correct call count
    
    // FYI: Previously we were emitting socket events from here, which caused duplicates
  };

  // Socket event handlers
  useEffect(() => {
    if (!socket) return;

    // Set up socket event handlers for this lead

    // Function to handle call added events
    const handleCallAdded = (data) => {
      if (data.leadId === lead.leadId || data.leadId === lead._id) {
        // Track event timestamp to handle out-of-order events
        const leadId = lead.leadId || lead._id;
        const eventTime = new Date(data.timestamp || Date.now()).getTime();
        const lastTime = lastEventTimestampRef.current[leadId] || 0;
        
        // Only process if this event is newer than the last one we processed
        const newCount = data.callCount !== undefined ? Number(data.callCount) : null;
        const shouldProcessEvent = eventTime > lastTime || (newCount !== null && newCount > localCallCount);
        
        if (shouldProcessEvent) {
          // Update the timestamp reference
          lastEventTimestampRef.current[leadId] = Math.max(eventTime, lastTime);
          
          console.log(`[NewKBLeadCard] Processing call:added event:`, {
            eventTime: new Date(eventTime).toLocaleTimeString(),
            lastProcessedTime: lastTime ? new Date(lastTime).toLocaleTimeString() : 'none',
            leadId: data.leadId,
            callCount: data.callCount !== undefined ? data.callCount : 'not specified',
            rescheduledTo: data.rescheduledTo !== undefined ? (data.rescheduledTo || 'null/cleared') : 'not specified',
            nextFollowUpTime: data.nextFollowUpTime !== undefined ? (data.nextFollowUpTime || 'null/cleared') : 'not specified'
          });
          
          // Update the local state with the new count
          if (data.callCount !== undefined) {
            setLocalCallCount(Number(data.callCount));
          }
          
          // Update last call time if present in the event
          if (data.timestamp) {
            setLocalLastCallTime(data.timestamp);
          }
          
          // Update follow-up time if included in the event
          // Need to handle both defined values and explicit null (cleared follow-up)
          if (data.rescheduledTo !== undefined || data.nextFollowUpTime !== undefined) {
            // Prefer nextFollowUpTime if available, otherwise use rescheduledTo
            const newFollowUpTime = data.nextFollowUpTime !== undefined ? 
              data.nextFollowUpTime : data.rescheduledTo;
              
            console.log(`[NewKBLeadCard] Updating follow-up time from call:added:`, {
              oldTime: localNextFollowUpTime,
              newTime: newFollowUpTime,
              isCleared: newFollowUpTime === null
            });
            
            setLocalNextFollowUpTime(newFollowUpTime);
          }
        }
      }
    };
    
    // Function to handle lead updated events
    const handleLeadUpdated = (data) => {
      if (data.leadId === lead.leadId || data.leadId === lead._id) {
        // Track event timestamp to handle out-of-order events
        const leadId = lead.leadId || lead._id;
        const eventTime = new Date(data.timestamp || Date.now()).getTime();
        const lastTime = lastEventTimestampRef.current[leadId] || 0;
        
        // Extract the full updated lead object if available
        const updatedLead = data;
        
        // Only process if this event is newer than the last one we processed
        // or if we're seeing a higher call count
        const newCount = (updatedLead && updatedLead.callCount !== undefined) ? 
          Number(updatedLead.callCount) : 
          (data.callCount !== undefined ? Number(data.callCount) : null);

        const shouldProcessEvent = eventTime > lastTime || (newCount !== null && newCount > localCallCount);
        
        if (shouldProcessEvent) {
          // Update the timestamp reference
          lastEventTimestampRef.current[leadId] = Math.max(eventTime, lastTime);
          
          console.log(`[NewKBLeadCard] Processing lead:updated event:`, {
            eventTime: new Date(eventTime).toLocaleTimeString(),
            lastProcessedTime: lastTime ? new Date(lastTime).toLocaleTimeString() : 'none',
            currentCallCount: localCallCount,
            newCallCount: newCount,
            hasNextFollowUpTime: updatedLead && updatedLead.nextFollowUpTime !== undefined,
            nextFollowUpTime: updatedLead && updatedLead.nextFollowUpTime !== undefined ? 
              (updatedLead.nextFollowUpTime || 'null/cleared') : 'not in event'
          });

          // Update counts if included (even if we have full lead update)
          if (updatedLead && updatedLead.callCount !== undefined) {
            console.log(`[NewKBLeadCard] UPDATING call count from ${localCallCount} to ${updatedLead.callCount} via lead:updated`);
            setLocalCallCount(Number(updatedLead.callCount));
          } else if (data.callCount !== undefined) {
            setLocalCallCount(Number(data.callCount));
          }
          
          if (updatedLead && updatedLead.commentCount !== undefined) {
            setLocalCommentCount(Number(updatedLead.commentCount));
          } else if (data.commentCount !== undefined) {
            setLocalCommentCount(Number(data.commentCount));
          }
        
          if (updatedLead && updatedLead.taskCount !== undefined) {
            setLocalTaskCount(Number(updatedLead.taskCount));
          } else if (data.taskCount !== undefined) {
            setLocalTaskCount(Number(data.taskCount));
          }
          
          // Update follow-up time if included
          // Need to handle both defined values and explicit null (cleared follow-up)
          if (updatedLead && updatedLead.nextFollowUpTime !== undefined) {
            console.log(`[NewKBLeadCard] Updating follow-up time from lead:updated:`, {
              oldTime: localNextFollowUpTime,
              newTime: updatedLead.nextFollowUpTime,
              isCleared: updatedLead.nextFollowUpTime === null
            });
            setLocalNextFollowUpTime(updatedLead.nextFollowUpTime);
          } else if (data.nextFollowUpTime !== undefined) {
            console.log(`[NewKBLeadCard] Updating follow-up time from data.nextFollowUpTime:`, {
              oldTime: localNextFollowUpTime,
              newTime: data.nextFollowUpTime,
              isCleared: data.nextFollowUpTime === null
            });
            setLocalNextFollowUpTime(data.nextFollowUpTime);
          }
        }
      }
    };

    // Function to handle backup call count update events
    const handleCallCountUpdated = (data) => {
      if (data.leadId === lead.leadId || data.leadId === lead._id) {
        // Track event timestamp to handle out-of-order events
        const leadId = lead.leadId || lead._id;
        const eventTime = new Date(data.timestamp || Date.now()).getTime();
        const lastTime = lastEventTimestampRef.current[leadId] || 0;
        
        // For backup events, we'll use them even if older, but only if they have a higher count
        if (data.callCount !== undefined) {
          const newCount = Number(data.callCount);
          // Only update if the new count is higher than our current local count
          if (newCount > localCallCount) {
            // Update timestamp reference after accepting this event
            lastEventTimestampRef.current[leadId] = Math.max(eventTime, lastTime);
            setLocalCallCount(newCount);
          } else {
            console.log(`[NewKBLeadCard] BACKUP - Ignoring count ${newCount} as it's not higher than current ${localCallCount}`);
          }
        }
      }
    };
    
    // Register event listeners
    socket.on('call:added', handleCallAdded);
    socket.on('lead:updated', handleLeadUpdated);
    socket.on('callCount:updated', handleCallCountUpdated);

    // Cleanup function
    return () => {
      socket.off('call:added', handleCallAdded);
      socket.off('lead:updated', handleLeadUpdated);
      socket.off('callCount:updated', handleCallCountUpdated);
    };
  }, [socket, lead.leadId, lead._id]);

  // Format relative time for follow-up
  const getRelativeTime = (timeString) => {
    if (!timeString) return 'No follow-up';
    
    const time = moment(timeString);
    const now = moment();
    
    if (time.isBefore(now)) {
      return `Overdue: ${time.fromNow()}`;
    }
    return `Follow-up: ${time.fromNow()}`;
  };

  // Determine if follow-up is overdue or missing
  const isOverdue = localNextFollowUpTime ? 
    moment(localNextFollowUpTime).isBefore(moment()) : false;
  const hasNoFollowUp = !localNextFollowUpTime;
  
  // Card styling based on state
  const bgColor = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const followUpColor = hasNoFollowUp || isOverdue ? 'red.500' : 'green.500';

  // Format dates
  const formatDate = (dateString) => {
    if (!dateString) return '';
    return moment(dateString).format('MMM D, YYYY');
  };
  
  // Format currency using the Indian system (lakhs and crores)

  // Get display name - could be brideAndGroomName or name
  // Handle case where lead.name could be an object instead of a string
  const displayName = lead.brideAndGroomName || 
    (typeof lead.name === 'string' ? lead.name : 
     typeof lead.name === 'object' && lead.name?.name ? lead.name.name : 'Unnamed Lead');
  
  // Get first event date if available
  const firstEventDate = lead.eventDates && lead.eventDates.length > 0 ? lead.eventDates[0] : null;
  
  // Get city if available
  const city = lead.locations && lead.locations.cities && lead.locations.cities.length > 0 ? 
    lead.locations.cities[0] : null;
  
  // Get budget if available
  const budget = lead.budget && lead.budget.amount ? 
    formatIndianCurrency(lead.budget.amount) : null;

  // Logging the assignment user info for debugging
  useEffect(() => {
    if (lead.assignedTo) {
      console.log('[NewKBLeadCard] Assigned to:', lead.assignedTo, 'Display name:', getUserDisplayName(lead.assignedTo));
    }
  }, [lead.assignedTo, getUserDisplayName]);

  return (
    <>
      <NewKBDragWrapper
        id={lead.leadId}
        index={index}
        isDragDisabled={isDragDisabled || !lead.leadId}
        onDoubleClick={() => setIsEditModalOpen(true)}
      >
        {/* Top Row: Name and Action Buttons */}
        <Flex justify="space-between" align="center" mb={0.5}>
          {/* Lead Name */}
          <Text fontWeight="semibold" fontSize="xs" isTruncated maxWidth="75%">
            {displayName}
          </Text>
          
          {/* Action Buttons */}
          <Flex>
            <Tooltip 
              hasArrow 
              openDelay={300}
              label={
                <Box>
                  <Text fontWeight="bold">Record Call</Text>
                  {localLastCallTime ? (
                    <Text fontSize="xs" mt={1}>
                      Last call: {moment(localLastCallTime).format('MMM D, YYYY h:mm A')}
                    </Text>
                  ) : localCallCount > 0 ? (
                    <Text fontSize="xs" mt={1}>Last call time not available</Text>
                  ) : (
                    <Text fontSize="xs" mt={1}>No previous calls</Text>
                  )}
                </Box>
              }
            >
              <IconButton
                icon={<PhoneIcon boxSize={3} />}
                size="xs"
                variant="ghost"
                colorScheme="blue"
                mr={1}
                onClick={(e) => {
                  e.stopPropagation();
                  setIsCallModalOpen(true);
                }}
              />
            </Tooltip>
            
            <Tooltip label="Edit Lead">
              <IconButton
                icon={<EditIcon />}
                size="xs"
                variant="ghost"
                colorScheme="gray"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsEditModalOpen(true);
                }}
              />
            </Tooltip>
          </Flex>
        </Flex>
        
        {/* Second Row: Follow-up Time and No Calls Alert */}
        <Flex justify="space-between" align="center" mb={1}>
          <Text 
            fontSize="xs" 
            color={followUpColor} 
            fontWeight={isOverdue ? "bold" : "normal"}
          >
            {getRelativeTime(localNextFollowUpTime)}
          </Text>
          
          {/* Show No Calls Yet message when call count is 0 */}
          {localCallCount === 0 && (
            <Text
              fontSize="xs"
              color="red.500"
              fontWeight="medium"
            >
              No Calls Yet
            </Text>
          )}
        </Flex>
        
        {/* Additional Lead Info - Only shown if available */}
        {(firstEventDate || city || budget) && (
          <Flex fontSize="xs" mb={1} justify="flex-start" align="center" wrap="wrap" gap={2}>
            {firstEventDate && (
              <Flex align="center">
                <CalendarIcon mr={1} color="blue.500" boxSize={2.5} />
                <Text color="gray.600" fontSize="xs">{formatDate(firstEventDate)}</Text>
              </Flex>
            )}
            
            {city && (
              <Flex align="center">
                <InfoIcon mr={1} color="purple.500" boxSize={2.5} />
                <Text color="gray.600" fontSize="xs">{city}</Text>
              </Flex>
            )}
            
            {budget && (
              <Badge colorScheme="green" variant="subtle" fontSize="xs">
                {budget}
              </Badge>
            )}
          </Flex>
        )}
        
        {/* Badge Row */}
        <Flex wrap="wrap" gap={1} mb={1}>
          <Badge colorScheme="blue" size="sm" fontSize="xs">
            <Flex align="center">
              <PhoneIcon mr={1} boxSize={2.5} />
              {localCallCount}
            </Flex>
          </Badge>
          
          {/* Alert Icon for Incomplete Requirements */}
          {lead.incompleteRequirements && lead.incompleteRequirements.length > 0 && (
            <Tooltip label={
              <Box p={1}>
                <Text fontWeight="bold" mb={1}>Missing Requirements:</Text>
                <List spacing={1} fontSize="xs">
                  {lead.incompleteRequirements.map((req, idx) => (
                    <ListItem key={idx}>
                      <ListIcon as={CloseIcon} color="red.500" boxSize={2} />
                      {req.name}: {req.description}
                    </ListItem>
                  ))}
                </List>
              </Box>
            } hasArrow placement="top">
              <Badge colorScheme="red" size="sm" fontSize="xs">
                <Flex align="center">
                  <WarningIcon mr={1} boxSize={2.5} />
                  {lead.incompleteRequirements.length}
                </Flex>
              </Badge>
            </Tooltip>
          )}
          
          <Badge colorScheme="teal" size="sm" fontSize="xs">
            <Flex align="center">
              <ChatIcon mr={1} boxSize={2.5} />
              {localCommentCount}
            </Flex>
          </Badge>
          
          <Badge colorScheme="orange" size="sm" fontSize="xs">
            <Flex align="center">
              <CheckIcon mr={1} boxSize={2.5} />
              {localTaskCount}
            </Flex>
          </Badge>
          
          {lead.tags && lead.tags.length > 0 && (
            <Tooltip 
              label={lead.tags.join(', ')} 
              placement="top" 
              hasArrow
            >
              <Badge colorScheme="purple" size="sm" fontSize="xs">
                {lead.tags[0]}
                {lead.tags.length > 1 && ` +${lead.tags.length - 1}`}
              </Badge>
            </Tooltip>
          )}
        </Flex>
        
        {/* Bottom Row: Added Date, Assigned To, and Priority */}
        <Flex justify="space-between" align="center" fontSize="xs">
          <Flex align="center">
            <Text color="gray.500">
              {formatDate(lead.dateAdded)}
            </Text>
            {lead.assignedTo && (
              <Text color="gray.500" ml={2}>
                • {getUserDisplayName(lead.assignedTo) || (typeof lead.assignedTo === 'string' ? lead.assignedTo.split('@')[0] : 'Assigned')}
              </Text>
            )}
          </Flex>
          
          {lead.priority && (
            <Badge colorScheme={
              lead.priority === 'high' ? 'red' : 
              lead.priority === 'medium' ? 'orange' : 'green'
            } size="sm" fontSize="xs">
              {lead.priority}
            </Badge>
          )}
        </Flex>
      </NewKBDragWrapper>
      
      {/* Modals */}
      {isCallModalOpen && (
        <NewKBRecordCallModal
          isOpen={isCallModalOpen}
          onClose={() => setIsCallModalOpen(false)}
          lead={lead}
          onSuccess={handleCallSuccess}
        />
      )}
      
      {isEditModalOpen && (
        <NewKBLeadModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          leadId={lead.leadId}
          onLeadAdded={(updatedLead) => {
            console.log('[NewKBLeadCard] Lead updated from modal:', updatedLead);
            if (onLeadUpdate) {
              onLeadUpdate(updatedLead);
            }
          }}
        />
      )}
    </>
  );
};

// Use React.memo to prevent unnecessary re-renders
// The custom comparison function ensures we only re-render when THIS lead changes
const MemoizedNewKBLeadCard = React.memo(NewKBLeadCard, (prevProps, nextProps) => {
  // If the lead ID is different, definitely re-render
  if (prevProps.lead.leadId !== nextProps.lead.leadId) return false;
  
  // If key properties are the same, we can skip re-rendering
  // Compare stringified versions to handle deep equality
  const prevKey = JSON.stringify({
    name: prevProps.lead.brideAndGroomName,
    callCount: prevProps.lead.callCount,
    commentCount: prevProps.lead.commentCount,
    taskCount: prevProps.lead.taskCount,
    stage: typeof prevProps.lead.stage === 'object' ? prevProps.lead.stage._id : prevProps.lead.stage,
    _updateKey: prevProps.lead._updateKey,
    _lastUpdated: prevProps.lead._lastUpdated
  });
  
  const nextKey = JSON.stringify({
    name: nextProps.lead.brideAndGroomName,
    callCount: nextProps.lead.callCount,
    commentCount: nextProps.lead.commentCount,
    taskCount: nextProps.lead.taskCount,
    stage: typeof nextProps.lead.stage === 'object' ? nextProps.lead.stage._id : nextProps.lead.stage,
    _updateKey: nextProps.lead._updateKey,
    _lastUpdated: nextProps.lead._lastUpdated
  });
  
  // Return true if the keys are the same (meaning no re-render needed)
  // Return false if the keys are different (meaning we need to re-render)
  return prevKey === nextKey;
});

export default MemoizedNewKBLeadCard;
